import { Routes } from '@angular/router';
import { AutoPayDetailWrapperComponent } from 'libs/billing/auto-pay/detail/feature/src/lib/containers/auto-pay-detail-wrapper/auto-pay-detail-wrapper.component';
import { PaymentMethodsComponent } from 'libs/billing/paymentaccount/feature/src/lib/containers/payment-methods.component';
// Guards
import { Observable, of as observableOf } from 'rxjs';

import { AutoPaySetupWrapperComponent } from '@amfam/billing/auto-pay/feature';
import {
  PaymentMethodConfirmationComponent,
  PaymentMethodDeleteContainerComponent,
  PaymentMethodFeatureGuard
} from '@amfam/billing/payment-method/feature';
import { PrintConfirmationWrapperComponent } from '@amfam/billing/shared/feature';

import { FeatureFlagGuard } from '../core';
import { BillAccountContactConfirmationComponent } from './bill-account-contact/bill-account-contact-confirmation/bill-account-contact-confirmation.component';
import { BillAccountContactComponent } from './bill-account-contact/bill-account-contact.component';
import { BillAccountContactGuard } from './bill-account-contact/bill-account-contact.guard';
import { BillAccountDetailComponent } from './bill-account-detail/bill-account-detail.component';
import { BillAccountFaqComponent } from './bill-account-faq/bill-account-faq.component';
import { BillAccountRegisterWrapperComponent } from './bill-account-register-wrapper/bill-account-register-wrapper.component';
import { BillAccountSectionComponent } from './bill-account-section/bill-account-section.component';
import { BillAccountRegistrationGuard } from './shared/bill-account-registration-guard';
import { BillAccountScheduledPaymentGuard } from './shared/bill-account-scheduled-payment-guard';
import { BillAccountSectionGuard } from './shared/bill-account-section-guard';
import { DeepLinkGuard } from './shared/deep-link.guard';

export function param(params: any): Observable<string> {
  return observableOf(params.id);
}

export const BillingRoutes: Routes = [
  {
    path: 'billing',
    data: { breadcrumb: 'Billing & Payments' },
    children: [
      {
        path: '',
        component: BillAccountSectionComponent,
        canActivate: [BillAccountSectionGuard],
        data: { breadcrumb: false }
      },
      {
        path: 'one-time-pay',
        loadChildren: () =>
          import('@amfam/billing/one-time-pay/feature').then(m => m.BillingOneTimePayFeatureModule),
        data: { breadcrumb: 'lazy_loaded_module', feature: 'one-time-pay' },
        canActivate: [FeatureFlagGuard]
      },
      {
        path: 'auto-pay/detail',
        loadChildren: () =>
          import('@amfam/billing/auto-pay/detail/feature').then(
            m => m.BillingAutoPayDetailFeatureModule
          ),
        data: { breadcrumb: false, redirectRoute: '/autopay' }
      },
      {
        path: 'auto-pay',
        loadChildren: () =>
          import('@amfam/billing/auto-pay/feature').then(m => m.BillingAutoPayFeatureModule),
        data: {
          breadcrumb: 'lazy_loaded_module',
          redirectRoute: '/autopay'
        }
      },
      {
        path: 'payment-method',
        data: { breadcrumb: 'My Payment Methods' },
        children: [
          {
            path: '',
            data: { breadcrumb: 'Add or Edit Payment Method' },
            component: PaymentMethodsComponent,
            pathMatch: 'full'
          },
          {
            path: 'delete',
            data: { breadcrumb: 'Delete Payment Method' },
            component: PaymentMethodDeleteContainerComponent,
            canActivate: [PaymentMethodFeatureGuard],
            canDeactivate: [PaymentMethodFeatureGuard]
          },
          {
            path: 'confirmation',
            data: { breadcrumb: 'Confirmation' },
            component: PaymentMethodConfirmationComponent,
            canDeactivate: [PaymentMethodFeatureGuard]
          }
        ]
      },
      {
        path: 'manage-autopay',
        component: AutoPayDetailWrapperComponent,
        data: { breadcrumb: 'Manage autopay' }
      },
      {
        path: 'contact',
        data: { breadcrumb: 'Contact' },
        canActivate: [BillAccountContactGuard],
        children: [
          {
            path: '',
            component: BillAccountContactComponent,
            data: { breadcrumb: false }
          },
          {
            path: ':id',
            component: BillAccountContactComponent,
            data: { breadcrumb: false }
          }
        ]
      },
      {
        path: 'contactConfirmation',
        data: { breadcrumb: 'Confirmation' },
        component: BillAccountContactConfirmationComponent,
        canActivate: [BillAccountContactGuard]
      },
      {
        path: 'confirmation',
        data: { breadcrumb: 'Confirmation' },
        component: PrintConfirmationWrapperComponent
      },
      {
        path: 'faq',
        data: { breadcrumb: 'FAQ' },
        component: BillAccountFaqComponent
      },
      {
        path: 'register',
        data: { breadcrumb: 'Pay Electronically' },
        canDeactivate: [BillAccountRegistrationGuard],
        component: BillAccountRegisterWrapperComponent
      },
      {
        path: 'statementhistory/:id',
        canActivate: [DeepLinkGuard],
        data: { breadcrumb: getPrettyBillAccountNumber },
        component: BillAccountDetailComponent
      },
      {
        path: ':id',
        data: { breadcrumb: getPrettyBillAccountNumber },
        component: BillAccountDetailComponent
      },
      {
        path: 'editautopay/:billAccountNumber',
        component: AutoPaySetupWrapperComponent,
        canActivate: [DeepLinkGuard],
        data: { breadcrumb: 'lazy_loaded_module' }
      }
    ]
  }
];

// TODO get this out of here
export function getPrettyBillAccountNumber(params: any): Observable<string> {
  function transform(value: any): any {
    if (value !== undefined && value.length === 11) {
      let prettyString = '';
      for (let i = 0; i < value.length; i++) {
        prettyString += value[i];
        if (i === 2 || i === 5 || i === 8) {
          prettyString += '-';
        }
      }
      return prettyString;
    }
    return value;
  }

  return observableOf(`${transform(params.id)}`);
}

export const BillingRoutingGuards = [
  BillAccountSectionGuard,
  BillAccountRegistrationGuard,
  BillAccountScheduledPaymentGuard,
  PaymentMethodFeatureGuard
];
